import { FC, ReactNode, useContext } from 'react';
import { Page } from './ExamDataHeader.styles';
import { useTranslation } from 'react-i18next';
import { Stack, Typography, LuxSelectablePill, Box } from '@gemini/fe-ds-core';
import { theme } from '@gemini/fe-ds-themes';
import ImportDeviceDataContext from '@context/ImportDeviceDataContext';
import { format } from '@gemini/fe-utils';
import { getAppointmentStatusColor } from '@utils/LuxColorUtil';

interface ExamDataHeaderProps {
  children?: ReactNode;
}

const ExamDataHeader: FC<ExamDataHeaderProps> = () => {
  const { t } = useTranslation();
  const { appointment } = useContext(ImportDeviceDataContext);

  return (
    <Page>
      <Box
        borderRadius={'8px'}
        borderColor={theme.palette.LUX.borders.color.borderNeutralLighter}
        sx={{
          borderStyle: 'solid',
          borderWidth: '1px',
          backgroundColor: theme.palette.LUX.background.neutral.surface,
        }}
      >
        <Stack
          flexDirection={'row'}
          gap={'2rem'}
          padding={'1rem 1rem 1.5rem 1rem'}
        >
          <Stack flexDirection={'column'} gap={'0.5rem'}>
            <Typography
              variant="textMdRegular"
              color={theme.palette.LUX.text.generic.textNeutralDarker}
            >
              {t('examImport.examData.patientName')}
            </Typography>
            <Typography variant="textMdRegular">
              {appointment?.patientName}
            </Typography>
          </Stack>
          <Stack flexDirection={'column'} gap={'0.5rem'}>
            <Typography
              variant="textMdRegular"
              color={theme.palette.LUX.text.generic.textNeutralDarker}
            >
              {t('examImport.examData.dob')}
            </Typography>
            <Typography variant="textMdRegular">
              {appointment?.patientBirthDate
                ? format(appointment.patientBirthDate)
                : ''}
            </Typography>
          </Stack>
          <Stack flexDirection={'column'} gap={'0.5rem'}>
            <Typography
              variant="textMdRegular"
              color={theme.palette.LUX.text.generic.textNeutralDarker}
            >
              {t('examImport.examData.patientId')}
            </Typography>
            <Typography variant="textMdRegular">
              {appointment?.patientId}
            </Typography>
          </Stack>
          <Stack flexDirection={'column'} gap={'0.5rem'}>
            <Typography
              variant="textMdRegular"
              color={theme.palette.LUX.text.generic.textNeutralDarker}
            >
              {t('examImport.examData.encounterDate')}
            </Typography>
            <Typography variant="textMdRegular">
              {appointment?.date ? format(appointment.date) : ''}
            </Typography>
          </Stack>
          <Stack flexDirection={'column'} gap={'0.5rem'}>
            <Typography
              variant="textMdRegular"
              color={theme.palette.LUX.text.generic.textNeutralDarker}
            >
              {t('examImport.examData.providerName')}
            </Typography>
            <Typography variant="textMdRegular">
              {appointment?.providerName}
            </Typography>
          </Stack>
          <Stack flexDirection={'column'} gap={'0.5rem'}>
            <Typography
              variant="textMdRegular"
              color={theme.palette.LUX.text.generic.textNeutralDarker}
            >
              {t('examImport.examData.examType')}
            </Typography>
            <LuxSelectablePill
              selectable={false}
              color="info"
              text={appointment?.examType ?? ''}
            />
          </Stack>
          <Stack flexDirection={'column'} gap={'0.5rem'}>
            <Typography
              variant="textMdRegular"
              color={theme.palette.LUX.text.generic.textNeutralDarker}
            >
              {t('examImport.examData.examStatus')}
            </Typography>
            <LuxSelectablePill
              selectable={false}
              color={getAppointmentStatusColor(appointment?.examStatus ?? '')}
              text={appointment?.examStatus ?? ''}
            />
          </Stack>
        </Stack>
      </Box>
    </Page>
  );
};

export default ExamDataHeader;
